// Country data with flags, currencies, and symbols for mobile money supported countries
export interface CountryData {
  code: string;
  name: string;
  flag: string;
  currency: string;
  symbol: string;
  dialCode: string;
}

export const COUNTRIES: Record<string, CountryData> = {
  KE: {
    code: 'KE',
    name: 'Kenya',
    flag: '🇰🇪',
    currency: 'KES',
    symbol: 'KSh',
    dialCode: '+254',
  },
  TZ: {
    code: 'TZ',
    name: 'Tanzania',
    flag: '🇹🇿',
    currency: 'TZS',
    symbol: 'TSh',
    dialCode: '+255',
  },
  UG: {
    code: 'UG',
    name: 'Uganda',
    flag: '🇺🇬',
    currency: 'UGX',
    symbol: 'UGX',
    dialCode: '+256',
  },
  RW: {
    code: 'RW',
    name: 'Rwanda',
    flag: '🇷🇼',
    currency: 'RWF',
    symbol: 'FRw',
    dialCode: '+250',
  },
  BI: {
    code: 'BI',
    name: 'Burundi',
    flag: '🇧🇮',
    currency: 'BIF',
    symbol: 'FBu',
    dialCode: '+257',
  },
  ZM: {
    code: 'ZM',
    name: 'Zambia',
    flag: '🇿🇲',
    currency: 'ZMW',
    symbol: 'ZK',
    dialCode: '+260',
  },
  MW: {
    code: 'MW',
    name: 'Malawi',
    flag: '🇲🇼',
    currency: 'MWK',
    symbol: 'MK',
    dialCode: '+265',
  },
  BW: {
    code: 'BW',
    name: 'Botswana',
    flag: '🇧🇼',
    currency: 'BWP',
    symbol: 'P',
    dialCode: '+267',
  },
  CM: {
    code: 'CM',
    name: 'Cameroon',
    flag: '🇨🇲',
    currency: 'XAF',
    symbol: 'XAF',
    dialCode: '+237',
  },
  GH: {
    code: 'GH',
    name: 'Ghana',
    flag: '🇬🇭',
    currency: 'GHS',
    symbol: 'GH₵',
    dialCode: '+233',
  },
  SS: {
    code: 'SS',
    name: 'South Sudan',
    flag: '🇸🇸',
    currency: 'SSP',
    symbol: '£',
    dialCode: '+211',
  },
  CI: {
    code: 'CI',
    name: 'Côte d\'Ivoire',
    flag: '🇨🇮',
    currency: 'XOF',
    symbol: 'CFA',
    dialCode: '+225',
  },
  SN: {
    code: 'SN',
    name: 'Senegal',
    flag: '🇸🇳',
    currency: 'XOF',
    symbol: 'CFA',
    dialCode: '+221',
  },
  ML: {
    code: 'ML',
    name: 'Mali',
    flag: '🇲🇱',
    currency: 'XOF',
    symbol: 'CFA',
    dialCode: '+223',
  },
  BF: {
    code: 'BF',
    name: 'Burkina Faso',
    flag: '🇧🇫',
    currency: 'XOF',
    symbol: 'CFA',
    dialCode: '+226',
  },
  BJ: {
    code: 'BJ',
    name: 'Benin',
    flag: '🇧🇯',
    currency: 'XOF',
    symbol: 'CFA',
    dialCode: '+229',
  },
  NG: {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬',
    currency: 'NGN',
    symbol: '₦',
    dialCode: '+234',
  },
  ZA: {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    currency: 'ZAR',
    symbol: 'R',
    dialCode: '+27',
  },
  CD: {
    code: 'CD',
    name: 'Democratic Republic of Congo',
    flag: '🇨🇩',
    currency: 'CDF',
    symbol: 'FC',
    dialCode: '+243',
  },
  ZW: {
    code: 'ZW',
    name: 'Zimbabwe',
    flag: '🇿🇼',
    currency: 'ZWL',
    symbol: '$',
    dialCode: '+263',
  },
  ET: {
    code: 'ET',
    name: 'Ethiopia',
    flag: '🇪🇹',
    currency: 'ETB',
    symbol: 'Br',
    dialCode: '+251',
  },
};

// Default fallback country (Kenya as it's a major mobile money market)
const DEFAULT_COUNTRY = COUNTRIES.KE;

// Registration fees are billed in the user's local currency.
// Base: KSh 250 (Kenya). Other countries are set to equivalent amounts accounting for FX rates.
export const COUNTRY_REGISTRATION_FEES: Record<string, { currency: string; symbol: string; amount: number }> = {
  KE: { currency: 'KES', symbol: 'KSh', amount: 250 },
  TZ: { currency: 'TZS', symbol: 'TSh', amount: 6600 },
  UG: { currency: 'UGX', symbol: 'UGX', amount: 10000 },
  RW: { currency: 'RWF', symbol: 'FRw', amount: 4000 },
  NG: { currency: 'NGN', symbol: '₦', amount: 4000 },
  GH: { currency: 'GHS', symbol: 'GH₵', amount: 40 },
  ZM: { currency: 'ZMW', symbol: 'ZK', amount: 50 },
  BI: { currency: 'BIF', symbol: 'FBu', amount: 7750 },
  MW: { currency: 'MWK', symbol: 'MK', amount: 4480 },
  BW: { currency: 'BWP', symbol: 'P', amount: 35.18 },
  CM: { currency: 'XAF', symbol: 'XAF', amount: 1487 },
  SS: { currency: 'SSP', symbol: '£', amount: 14774 },
  CI: { currency: 'XOF', symbol: 'CFA', amount: 1487 },
  SN: { currency: 'XOF', symbol: 'CFA', amount: 1487 },
  ML: { currency: 'XOF', symbol: 'CFA', amount: 1487 },
  BF: { currency: 'XOF', symbol: 'CFA', amount: 1487 },
  BJ: { currency: 'XOF', symbol: 'CFA', amount: 1487 },
  ZA: { currency: 'ZAR', symbol: 'R', amount: 42 },
  CD: { currency: 'CDF', symbol: 'FC', amount: 6051 },
  ZW: { currency: 'ZWL', symbol: '$', amount: 68.88 },
  ET: { currency: 'ETB', symbol: 'Br', amount: 419 },
};

export function getRegistrationFeeForCountry(countryCode: string): { currency: string; symbol: string; amount: number } {
  const code = (countryCode || 'KE').toUpperCase();
  return COUNTRY_REGISTRATION_FEES[code] ?? COUNTRY_REGISTRATION_FEES.KE;
}

/**
 * Get country data from browser timezone
 * This is a client-side approximation of user location
 */
export function getCountryFromTimezone(): CountryData {
  if (typeof window === 'undefined') {
    return DEFAULT_COUNTRY;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map common timezones to countries
    const timezoneMap: Record<string, string> = {
      'Africa/Nairobi': 'KE',
      'Africa/Accra': 'GH',
      'Africa/Lagos': 'NG',
      'Africa/Dar_es_Salaam': 'TZ',
      'Africa/Kampala': 'UG',
      'Africa/Johannesburg': 'ZA',
      'Africa/Kigali': 'RW',
      'Africa/Abidjan': 'CI',
      'Africa/Dakar': 'SN',
      'Africa/Bamako': 'ML',
      'Africa/Ouagadougou': 'BF',
      'Africa/Douala': 'CM',
      'Africa/Lubumbashi': 'CD',
      'Africa/Lusaka': 'ZM',
      'Africa/Harare': 'ZW',
      'Africa/Addis_Ababa': 'ET',
    };

    const countryCode = timezoneMap[timezone];
    if (countryCode && COUNTRIES[countryCode]) {
      return COUNTRIES[countryCode];
    }
  } catch (error) {
    console.error('Error detecting country from timezone:', error);
  }

  return DEFAULT_COUNTRY;
}

/**
 * Get country data from browser locale
 */
export function getCountryFromLocale(): CountryData {
  if (typeof window === 'undefined') {
    return DEFAULT_COUNTRY;
  }

  try {
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const countryCode = locale.split('-')[1]?.toUpperCase();
    
    if (countryCode && COUNTRIES[countryCode]) {
      return COUNTRIES[countryCode];
    }
  } catch (error) {
    console.error('Error detecting country from locale:', error);
  }

  return DEFAULT_COUNTRY;
}

/**
 * Get country data by country code
 */
export function getCountryByCode(code: string): CountryData | undefined {
  return COUNTRIES[code.toUpperCase()];
}

/**
 * Get all supported countries
 */
export function getAllCountries(): CountryData[] {
  return Object.values(COUNTRIES);
}

/**
 * Detect user's country using multiple methods
 * Priority: localStorage > timezone > locale > default
 */
export function detectUserCountry(): CountryData {
  if (typeof window === 'undefined') {
    return DEFAULT_COUNTRY;
  }

  try {
    // Check localStorage first (user may have manually selected)
    const storedCountry = localStorage.getItem('userCountry');
    if (storedCountry && COUNTRIES[storedCountry]) {
      return COUNTRIES[storedCountry];
    }

    // Try timezone detection
    const timezoneCountry = getCountryFromTimezone();
    if (timezoneCountry !== DEFAULT_COUNTRY) {
      return timezoneCountry;
    }

    // Try locale detection
    const localeCountry = getCountryFromLocale();
    if (localeCountry !== DEFAULT_COUNTRY) {
      return localeCountry;
    }
  } catch (error) {
    console.error('Error detecting user country:', error);
  }

  return DEFAULT_COUNTRY;
}

/**
 * Save user's country preference
 */
export function saveUserCountry(countryCode: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('userCountry', countryCode);
  }
}

// Forex exchange rates (base: KES) — updated September 2026 (mid-market + 5)
export const EXCHANGE_RATES: Record<string, number> = {
  'KES': 1,
  'TZS': 25.36,
  'UGX': 33.76,
  'RWF': 16.36,
  'BIF': 28.14,
  'ZMW': 5.1727,
  'MWK': 18.37,
  'BWP': 5.105,
  'XAF': 9.370,
  'GHS': 5.0885,
  'SSP': 6.01,
  'XOF': 9.380,
  'NGN': 15.69,
  'ZAR': 5.1241,
  'CDF': 22.83,
  'ZWL': 7.48,
  'ETB': 6.238,
};

/**
 * Convert KES to another currency
 */
export function convertKesToCurrency(amountInKes: number, targetCurrency: string): number {
  const rate = EXCHANGE_RATES[targetCurrency];
  if (!rate) {
    console.warn(`No exchange rate found for ${targetCurrency}, using KES`);
    return amountInKes;
  }
  return amountInKes * rate;
}

/**
 * Get initial agent bonus in user's local currency
 */
export function getInitialAgentBonusInLocalCurrency(currencyCode: string): number {
  return convertKesToCurrency(100, currencyCode);
}
